import type { FilterType, SortableType } from "@/components/@types";
import { AiRewrite } from "@/components/organisms/AiRewrite/AiRewrite";
import { UtilityBar } from "@/components/organisms/UtilityBar";
import { flattenFilterOperators } from "@/components/organisms/UtilityBar/FilterChip/FilterChip.utils";
import { useAppRouteState } from "@/app/app/_/routing";
import { SEARCH_FILTER_KEY } from "@/graphql/filters";
import { State } from "@/state";
import { Typography } from "@/components/ui/typography";

/** What came back: the chips, the sort and the search, not yet on the bar. */
type Draft = {
  filters: FilterType[];
  sort: SortableType | null;
  search: string | null;
};

/**
 * A view's filters and sort, described rather than assembled.
 *
 * "What I spent at DoorDash in August over fifty, biggest first" is four chips
 * and a sort, each three pickers deep. The server is told the columns this bar
 * is built from - their operators, the values an enum takes, the records a
 * reference points at - and answers with chips as the bar holds them. The
 * preview is those chips, drawn and editable as they will be on the bar, so a
 * draft that is nearly right is finished by hand rather than asked again.
 *
 * Words that are in a record rather than a column - "DoorDash" is in a
 * transaction's merchant, which no chip filters by - are the view's search,
 * where it has one. Locked chips are the tab's own: they are not offered, and
 * a draft that is used leaves them where they are.
 */
export function UtilityBarDraft({ isSearchable }: { isSearchable: boolean }) {
  const { view } = useAppRouteState();
  const filterDefinitions = State((state) => state.filterDefinitions);
  const sortableDefinitions = State((state) => state.sortableDefinitions);
  const filters = State((state) => state.filters);
  const sortables = State((state) => state.sortables);

  const isOffered = (filter: FilterType) =>
    filter.isLocked !== true && filter.key !== SEARCH_FILTER_KEY;

  /** The draft's chips, with its search as the chip the search box writes. */
  const withSearch = (draft: Draft): FilterType[] => [
    ...(draft.search
      ? [
          {
            key: SEARCH_FILTER_KEY,
            type: "text",
            operator: "iContains",
            value: draft.search,
          } satisfies FilterType,
        ]
      : []),
    ...draft.filters,
  ];

  const sortName = (sort: SortableType) => {
    const definition = sortableDefinitions[sort.key];
    const direction = definition?.operators?.find(
      (operator) => operator.key === sort.value,
    );
    return `${definition?.name ?? sort.key}, ${(direction?.name ?? sort.value).toLowerCase()}`;
  };

  return (
    <AiRewrite<Draft>
      kind="data_view"
      label="Describe what to show"
      placeholder="What should this show? e.g. what I spent at DoorDash in August over fifty, biggest first"
      current={{
        view,
        columns: Object.entries(filterDefinitions)
          // ``self`` is the record itself, picked from the list rather than
          // named: a transaction has nothing it is called.
          .filter(([key]) => key !== "self")
          .map(([key, definition]) => ({
            key,
            name: definition.name,
            type: definition.type,
            operators: flattenFilterOperators(definition.operators).map(
              (operator) => operator.key,
            ),
            ...((definition.type === "enum" || definition.type === "enums") && {
              options: (
                definition.getValueOptions?.(undefined) as
                  | { enum: { value: string }[] }
                  | undefined
              )?.enum.map((option) => option.value),
            }),
            ...(definition.referenceTypename && {
              refersTo: definition.referenceTypename,
            }),
          })),
        sortable: Object.entries(sortableDefinitions).map(
          ([key, definition]) => ({ key, name: definition.name }),
        ),
        filters: filters.filter(isOffered).map((filter) => ({
          column: filter.key,
          operator: filter.operator,
          value: filter.value,
        })),
        searchable: isSearchable,
        search:
          filters.find((filter) => filter.key === SEARCH_FILTER_KEY)?.value ??
          null,
        sort: sortables[0]
          ? { column: sortables[0].key, direction: sortables[0].value }
          : null,
      }}
      preview={(draft, change) => (
        <div
          // The bar scrolls sideways on a view; in a box this narrow the chips
          // wrap instead, so every one of them can be read - and a chip wider
          // than the box (a date range, in a wider font) wraps its parts.
          className="flex flex-col gap-2 [&>[data-utility-bar]]:flex-wrap [&>[data-utility-bar]]:overflow-x-visible [&>[data-utility-bar]>*]:h-auto [&>[data-utility-bar]>*]:max-w-full [&>[data-utility-bar]>*]:flex-wrap"
        >
          {/* The search rides in the bar's filters, as it does on the view,
              so the preview draws it as the same pinned chip. */}
          <UtilityBar
            includeSort={false}
            disablePadding
            filters={withSearch(draft)}
            filterDefinitions={filterDefinitions}
            onFiltersChange={(next) =>
              change({
                ...draft,
                search:
                  (next.find((filter) => filter.key === SEARCH_FILTER_KEY)
                    ?.value as string | undefined) ?? null,
                filters: next.filter(
                  (filter) => filter.key !== SEARCH_FILTER_KEY,
                ),
              })
            }
          />
          {draft.sort && (
            <Typography level="body-sm" data-testid="utility-bar-draft-sort">
              Sorted by {sortName(draft.sort)}
            </Typography>
          )}
        </div>
      )}
      onApply={(draft) =>
        State.setState({
          filters: [
            ...State.getState().filters.filter(
              (filter) => filter.isLocked === true,
            ),
            ...withSearch(draft),
          ],
          sortables: draft.sort ? [draft.sort] : [],
        })
      }
    />
  );
}
